import type { CostCode, PurchaseOrderDeliveryStatus } from '@prisma/client';
import type { ColDef, ISetFilterParams } from 'ag-grid-enterprise';
import { TimecardEntryType } from '@prisma/client';
import { DateHelper } from '@utils/date.helper';
import { roundAmount } from '@utils/math';
import { formatInches, formatUSD, titlifyEnum } from '@utils/stringHelpers';
import { trpc } from '@utils/trpc';
import { isNotNullish } from '@utils/types';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useState } from 'react';
import { purchaseOrderStatusClasses } from '~/components/pages/jobs/purchase-orders/PurchaseOrderStatusBadge';
import { FAIcon } from '~/components/utils/FAIcons';
import { RelativeTime } from '~/components/utils/RelativeTime';
import type { CustomReportColumnContext } from '~/components/reporting/customReportState';
import type { IUserState } from '~/react_context/UserContext';
import { useUserContext } from '~/react_context/UserContext';
import Badge from '../Badge';

export type DeliveryChipDatum = {
	id: number;
	deliveryNumber: string;
	status: PurchaseOrderDeliveryStatus;
	expectedArrivalDate: Date | null;
	actualArrivalDate: Date | null;
};

export type DeliveryDateField = 'expectedArrivalDate' | 'actualArrivalDate';

export const earliestDeliveryDate = (deliveries: DeliveryChipDatum[] | undefined, key: DeliveryDateField): Date | null => {
	const times = (deliveries ?? [])
		.map((d) => d[key])
		.filter((d): d is Date => d != null)
		.map((d) => new Date(d).getTime());
	return times.length ? new Date(Math.min(...times)) : null;
};

export const latestDeliveryDate = (deliveries: DeliveryChipDatum[] | undefined, key: DeliveryDateField): Date | null => {
	const times = (deliveries ?? [])
		.map((d) => d[key])
		.filter((d): d is Date => d != null)
		.map((d) => new Date(d).getTime());
	return times.length ? new Date(Math.max(...times)) : null;
};

export const DeliveryDateChipsRenderer =
	(dateField: DeliveryDateField) =>
	({ value }: { value: DeliveryChipDatum[] | undefined }) => {
		const chips = (value ?? []).filter((d) => d[dateField] != null);
		if (!chips.length) return null;
		return (
			<div className="inline-flex flex-wrap items-center gap-1">
				{chips.map((d) => {
					const date = new Date(d[dateField]!).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
					const label = `${d.deliveryNumber} · ${date}`;
					return (
						<span
							key={d.id}
							title={titlifyEnum(d.status)}
							className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium ${purchaseOrderStatusClasses(d.status)}`}
						>
							{label}
						</span>
					);
				})}
			</div>
		);
	};

// 1/1/2025 format
export const dateFormatter = (params) => (params.value ? new Date(params.value).toLocaleDateString('en-US') : '');
// 1/1/2025, 10:00:00 PM format
export const dateTimeFormatter = (params) => (params.value ? new Date(params.value).toLocaleString('en-US') : '');
// Jan 1, 2025 format
export const shortDateFormatter = (params) =>
	params.value ? new Date(params.value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
// Jan 1, 2025 format, in the offset the value carries rather than the viewer's zone. For values the server
// already resolved to a specific timezone (the finance center's payment grids stamp the business tz), where
// re-rendering locally can shift the row a day off the range it was filtered by.
export const offsetDateFormatter = (params) =>
	params.value ? DateTime.fromISO(String(params.value), { setZone: true }).toFormat('LLL d, yyyy') : '';
// Jan 1, 2025, 10:00 AM format
export const shortDateTimeFormatter = (params) =>
	params.value
		? new Date(params.value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
		: '';
// "3 days ago" format
export const relativeDateFormatter = (params) => (params.value ? (DateTime.fromJSDate(new Date(params.value)).toRelative() ?? '') : '');
// "3 days ago" rendered as a <time> element whose title/dateTime carry the exact timestamp.
// Pair with `relativeDateFormatter` as the column's `valueFormatter` so sort/filter/export use the value.
export const RelativeDateRenderer = ({ value }: { value?: Date | string | number | null }) => <RelativeTime fallback="" value={value} />;

// Use as the `filterParams` of an `agDateColumnFilter` column. AG Grid's built-in date comparator compares the
// full timestamp against the filter's local midnight, so "Equals <day>" never matches a value that carries a time
// of day. This compares at day granularity instead, so picking a day matches every row stamped on that day.
export const dateFilterParams = {
	comparator: (filterLocalDateAtMidnight: Date, cellValue: Date | string) => DateHelper.comparator(filterLocalDateAtMidnight, new Date(cellValue)),
};

export const noteListFormatter = (params) => params.value?.join(', ') ?? '';

export const percentageFormatter = (params) => {
	if (params.value != null && !isNaN(params.value)) {
		return new Intl.NumberFormat('en-US', {
			style: 'percent',
			minimumFractionDigits: 2, // This ensures we always get two decimal places
			maximumFractionDigits: 2,
		}).format(parseFloat(params.value) / 100); // Divide by 100 to convert from string to percentage
	} else {
		return '';
	}
};

export const percentageFormatterScaleUp = (params) => {
	if (params.value != null && !isNaN(params.value)) {
		return new Intl.NumberFormat('en-US', {
			style: 'percent',
			minimumFractionDigits: 2, // This ensures we always get two decimal places
			maximumFractionDigits: 2,
		}).format(parseFloat(params.value)); // Divide by 100 to convert from string to percentage
	} else {
		return '';
	}
};

export const lengthFormatter = ({ value }) => (!!value ? formatInches(value) : '');

export const costCodeFormatter = ({ value }: { value?: CostCode }) =>
	value ? `${value.code}${value.description?.length ? `: ${value.description}` : ''}` : '';

export const valueParserForMoney = (params) => {
	if (params.newValue == null || params.newValue === '') return undefined;
	const cleanedValue = String(params.newValue).replace(/[$,]/g, '').trim();
	const parsed = parseFloat(cleanedValue);
	return isNaN(parsed) ? undefined : parsed;
};

export const JobLinkRenderer = ({ value, data }) => {
	// This is used across all reporting grids.
	// even on the jobs grid, so we need to use jobId here to render the link to go to the right place.
	if (data.job?.isTemplate || data.isStockPO) return <></>;
	return (
		<Link className="text-grey-500 underline cursor-pointer" target="_blank" href={`/jobs/${encodeURIComponent(data.jobId ?? data.id)}`}>
			{value}
		</Link>
	);
};

export const PaymentLinkRenderer = ({ value, data }) => (
	// This is used across all reporting grids.
	// even on the jobs grid, so we need to use jobId here to render the link to go to the right place.
	<Link className="text-grey-500 underline cursor-pointer" href={`/jobs/${encodeURIComponent(data.jobId)}/payments`}>
		{value}
	</Link>
);

export const transactionAmountsFormatter = ({ value }: { value?: { id: number; amount: number }[] }) =>
	value?.map((t) => formatUSD(t.amount)).join(', ') ?? '';

export const TransactionChipsRenderer = ({ data }: { data: { jobId: number; transactions: { id: number; amount: number }[] } }) => {
	if (!data?.transactions?.length) return null;
	return (
		<div className="flex flex-wrap gap-1 items-center py-0.5">
			{data.transactions.map((t) => (
				<Link
					key={t.id}
					className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
					href={`/jobs/${encodeURIComponent(data.jobId)}/payments`}
					target="_blank"
				>
					{formatUSD(t.amount)}
				</Link>
			))}
		</div>
	);
};

export const POLinkRenderer = ({ value, data }) => (
	<Link
		className="text-grey-500 underline cursor-pointer"
		href={
			data.job?.isTemplate || data.isStockPO
				? `/purchase-orders/${encodeURIComponent(data.purchaseOrderId ?? data.id)}`
				: `/jobs/${encodeURIComponent(data.jobId)}/purchase-orders/${encodeURIComponent(data.purchaseOrderId ?? data.id)}`
		}
	>
		{value}
	</Link>
);

export const CustomerLinkRenderer = ({ value, data }) => {
	if (data.job?.isTemplate || data.isStockPO) return <></>;
	return (
		<Link className="text-grey-500 underline cursor-pointer" href={`/customers/${encodeURIComponent(data.customerId)}`}>
			{value}
		</Link>
	);
};

export const EstimateLinkRenderer = ({ value, data }) => (
	<Link
		className="text-grey-500 underline cursor-pointer"
		href={`/jobs/${encodeURIComponent(data.jobId)}/${data.isChangeOrder ? 'change-orders' : 'estimates'}/${encodeURIComponent(data.version)}`}
	>
		{value}
	</Link>
);

export const InventoryLinkRenderer = ({ value, data }) => (
	<Link className="text-grey-500 underline cursor-pointer" href={`/materials/${encodeURIComponent(data.supplierInventoryId)}`}>
		{value}
	</Link>
);

export const MaterialLinkRenderer = ({ value, data }) => (
	<Link className="text-grey-500 underline cursor-pointer" href={`/materials/${encodeURIComponent(data.id)}`}>
		{value}
	</Link>
);

export const CostCodeCellRenderer = ({ value, data, api }) => {
	const handleChange = (event) => {
		const newValue = event.target.value;
		api.applyTransaction({ update: [{ ...data, costCode: newValue }] });
	};

	return (
		<select
			onChange={(event) => {
				handleChange(event);
			}}
			value={value}
		>
			<option value="Code1">Code1</option>
			<option value="Code2">Code2</option>
		</select>
	);
};

export const LocationLogRenderer = ({ value, data }) => {
	return (
		<Link
			className="text-grey-500 underline cursor-pointer"
			href={`/time-tracking-log/${data.userId}?date=${data.clockInTime
				.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
				.split('/')
				.join('-')}`}
		>
			View Tracking
		</Link>
	);
};

export const TimecardEntryTypeCellRenderer = ({ value }) => {
	const typeMapping = {
		BREAK: { label: 'Break', color: 'bg-yellow-100', icon: 'coffee' },
		NON_BILLABLE_BREAK: { label: 'Non-Billable Break', color: 'bg-orange-100', icon: 'pause' },
		FABRICATION: { label: 'In Office', color: 'bg-green-100', icon: 'building' },
		DRIVE: { label: 'Drive', color: 'bg-purple-100', icon: 'car' },
		OTHER: { label: 'Other', color: 'bg-gray-100', icon: 'ellipsis-h' },
	};

	const { label, color, icon } = typeMapping[value] || {};

	if (value === TimecardEntryType.REGULAR) return null;

	return (
		<div className="h-full flex items-center">
			<Badge size="sm" colors={color}>
				<FAIcon icon={icon} className="mr-1" />
				<span>{label}</span>
			</Badge>
		</div>
	);
};

export const SupplierInvoiceStatusCellRenderer = ({ value }: { value: string }) => {
	const statusMapping = {
		INVOICE_NEEDS_PAYMENT: { label: 'Needs Payment', color: 'bg-yellow-100', icon: 'exclamation-circle' },
		PAID: { label: 'Paid', color: 'bg-green-100', icon: 'check-circle' },
		CANCELLED: { label: 'Cancelled', color: 'bg-red-100', icon: 'times-circle' },
	};

	const { label, color, icon } = statusMapping[value] || {};

	return (
		<div className={`flex gap-2 px-1 py-1 pl-2 m-1 rounded-xs text-gray-600 text-sm ${color}`}>
			{icon && <FAIcon icon={icon} />}
			<span>{label || 'Unknown Status'}</span>
		</div>
	);
};

export const NoteCellRenderer = ({ value, data, api }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [note, setNote] = useState(value || '');

	const updateTimecardEntryMutation = trpc.timecardEntry.update.useMutation();

	const handleDoubleClick = () => {
		setIsEditing(true);
	};

	const handleChange = (event) => {
		setNote(event.target.value);
	};

	const handleBlur = () => {
		// Save the updated notes and stop editing mode
		api.applyTransaction({ update: [{ ...data, payrollNote: note }] });
		updateTimecardEntryMutation.mutate({ ...data, payrollNote: note });
		setIsEditing(false);
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			handleBlur();
		}
	};

	return isEditing ? (
		<input
			type="text"
			value={note}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyDown={handleKeyDown}
			className="w-full p-1 border rounded-sm"
			autoFocus
		/>
	) : (
		<div onDoubleClick={handleDoubleClick} className="w-30 flex cursor-pointer text-sm text-grey-500 whitespace-normal py-1">
			<span className="mr-2">{note || ''}</span>
			<FAIcon icon="edit" />
		</div>
	);
};

// Currency formatter function
export const currencyFormatter = (params) => {
	if (params.value != null && !isNaN(params.value)) {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(parseFloat(params.value));
	} else {
		return '';
	}
};

export const twoDecimalFormatter = (params) => {
	if (params.value != null && !isNaN(params.value)) {
		return `${roundAmount(params.value, 2)}`;
	} else {
		return '';
	}
};

export const titlifyFormatter = (params) => {
	return isNotNullish(params.value) ? titlifyEnum(params.value) : '';
};

export const SellPriceListCellRenderer = ({ value }) => {
	const sellPrices = value.filter((price) => price.sellPrice !== null);
	return (
		<>
			{sellPrices.slice(0, 2).map((price) => (
				<div key={price.id} className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">
					{`${price.variant ? `${price.variant}:` : ''} ${formatUSD(Number(price.sellPrice))}`}
				</div>
			))}

			{sellPrices.length > 2 && <div className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">+{sellPrices.length - 2}</div>}
		</>
	);
};

export const PriceListCellRenderer = ({ value }) => {
	return (
		<>
			{value.slice(0, 2).map((price) => (
				<div key={price.id} className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">
					{`${price.variant ? `${price.variant}:` : ''} ${formatUSD(Number(price.price))}`}
				</div>
			))}

			{value.length > 2 && <div className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">+{value.length - 2}</div>}
		</>
	);
};

export const VariantInventoryCellRenderer = ({ value }) => {
	const pricesWithInventory = value.filter((price) => price.variantInventories?.length);
	return (
		<>
			{pricesWithInventory.slice(0, 2).map((price) => (
				<div key={price.id} className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">
					{`${price.variant ? `${price.variant}:` : ''} ${price.variantInventories?.[0]?.onHand}`}
				</div>
			))}

			{pricesWithInventory.length > 2 && (
				<div className="bg-gray-100 p-1 text-xs rounded-md text-gray-900 m-0.5 w-fit">+{pricesWithInventory.length - 2}</div>
			)}
		</>
	);
};

export const stockPurchaseOrderConditionalRenderer = ({ value, data }) => {
	if (data.job?.isTemplate || data.isStockPO) return <></>;
	return <span>{value}</span>;
};

// The job's category by the business's own name, read through the pointer first: the enum
// column beside it is an inert placeholder on a job in a business-defined category.
export const JobCategoryCellRenderer = ({ data }) => {
	const {
		visibility: { getJobCategoryLabel },
	} = useUserContext();

	if (data.job?.isTemplate || data.isStockPO) return <></>;
	const category = data.job?.jobCategoryId ?? data.job?.businessCategory;

	return <span>{category ? getJobCategoryLabel(category) : ''}</span>;
};

/**
 * A job category set filter keyed by the category's row id while the cell shows its name, so a saved report's filter
 * survives the category being renamed.
 */
export const jobCategoryFilter = <TData,>({
	categoryOf,
	getJobCategoryId,
	getJobCategoryLabel,
}: {
	categoryOf: (data: TData | undefined) => number | string | null | undefined;
} & Pick<IUserState['visibility'], 'getJobCategoryId' | 'getJobCategoryLabel'>): Pick<
	ColDef<TData, string>,
	'context' | 'filter' | 'filterParams'
> => ({
	// The user context holds every live and hidden category, so an id it can't find is a deleted one.
	context: {
		formatFilterKey: (key) => (/^\d+$/.test(key) && getJobCategoryId(key) === undefined ? 'Deleted category' : getJobCategoryLabel(key) || key),
	} satisfies CustomReportColumnContext,
	filter: 'agSetColumnFilter',
	filterParams: {
		comparator: (a, b) => (a ?? '').localeCompare(b ?? ''),
		keyCreator: ({ data }) => {
			const category = categoryOf(data);
			return category ? String(getJobCategoryId(category) ?? category) : '';
		},
		valueFormatter: ({ value }) => value ?? '',
	} satisfies ISetFilterParams<TData, string>,
});

export const RelatedPOsRenderer = ({ data }) => {
	if (!data.relatedPurchaseOrderNumbers || data.relatedPurchaseOrderNumbers.length === 0) {
		return null;
	}

	// If there's only one PO, use the standard POLinkRenderer
	if (data.relatedPurchaseOrderNumbers.length === 1) {
		return POLinkRenderer({
			value: data.relatedPurchaseOrderNumbers[0],
			data: { ...data, purchaseOrderId: data.relatedPurchaseOrderIds[0] },
		});
	}

	// For multiple POs, render them as comma-separated links
	return (
		<>
			{data.relatedPurchaseOrderNumbers.map((poNumber, index) => (
				<span key={data.relatedPurchaseOrderIds[index]}>
					{POLinkRenderer({
						value: poNumber,
						data: { ...data, purchaseOrderId: data.relatedPurchaseOrderIds[index] },
					})}
					{index < data.relatedPurchaseOrderNumbers.length - 1 ? ', ' : ''}
				</span>
			))}
		</>
	);
};
